import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  TextField,
  Button,
  Typography,
  Collapse,
  Rating as MuiRating,
  ButtonGroup,
  Tooltip,
  Alert,
  Chip
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  Star,
  StarBorder,
  Send,
  Close
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { submitRating, fetchMessageRating } from '../store/slices/chatSlice';
import { isValidUUID } from '../utils/uuid';

interface MessageRatingProps {
  messageId: string | number;
  visible: boolean;
  onToggleVisibility: () => void;
}

const MessageRating: React.FC<MessageRatingProps> = ({ messageId, visible, onToggleVisibility }) => {
  const dispatch = useAppDispatch();
  const messageRatings = useAppSelector((state) => state.chat.messageRatings);
  const [ratingType, setRatingType] = useState<'thumbs' | 'stars' | null>(null);
  const [thumbsValue, setThumbsValue] = useState<number | null>(null);
  const [starsValue, setStarsValue] = useState<number>(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if message ID is valid UUID format
  const messageIdStr = messageId.toString();
  const isValidMessageId = isValidUUID(messageIdStr);
  
  // Get existing rating for this message
  const existingRating = messageRatings[messageIdStr];
  const hasExistingRating = !!existingRating;

  // Fetch existing rating when component mounts (only for valid UUIDs)
  useEffect(() => {
    if (!hasExistingRating && isValidMessageId) {
      dispatch(fetchMessageRating(messageIdStr));
    }
  }, [dispatch, messageIdStr, hasExistingRating, isValidMessageId]);

  const handleThumbsClick = (value: number) => {
    setRatingType('thumbs');
    setThumbsValue(value);
    setShowFeedback(true);
  };

  const handleStarsClick = () => {
    setRatingType('stars');
    setShowFeedback(true);
  };

  const handleSubmit = async () => {
    if (!ratingType) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      let ratingValue: number;
      if (ratingType === 'thumbs') {
        ratingValue = thumbsValue || 0;
      } else {
        ratingValue = starsValue;
      }
      
      await dispatch(submitRating({
        messageId,
        ratingType,
        ratingValue,
        feedbackText: feedbackText.trim() || undefined
      })).unwrap();
      
      setSubmitted(true);
      setTimeout(() => {
        onToggleVisibility();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to submit rating');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setRatingType(null);
    setThumbsValue(null);
    setStarsValue(0);
    setFeedbackText('');
    setShowFeedback(false);
    setError(null);
    onToggleVisibility();
  };

  // If message ID is not a valid UUID, don't show rating functionality
  if (!isValidMessageId) {
    return null;
  }

  // If there's an existing rating, show it instead of allowing new rating
  if (hasExistingRating) {
    return (
      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your rating:
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          {existingRating.rating_type === 'thumbs' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip 
                icon={existingRating.rating_value === 1 ? <ThumbUp /> : <ThumbDown />}
                label={existingRating.rating_value === 1 ? 'Thumbs Up' : 'Thumbs Down'}
                color={existingRating.rating_value === 1 ? 'success' : 'error'}
                size="small"
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Star color="warning" />
              <Typography variant="body2">{existingRating.rating_value}/5 stars</Typography>
            </Box>
          )}
        </Box>
        
        {existingRating.feedback_text && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Your feedback:
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
              "{existingRating.feedback_text}"
            </Typography>
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          You have already rated this message.
        </Typography>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ p: 1, bgcolor: 'success.light', borderRadius: 1, mt: 1 }}>
        <Alert severity="success" sx={{ bgcolor: 'transparent', py: 0 }}>
          Thank you for your feedback!
        </Alert>
      </Box>
    );
  }

  if (!visible) {
    return (
      <Button
        size="small"
        variant="outlined"
        onClick={onToggleVisibility}
        sx={{ mt: 1, fontSize: '0.75rem' }}
      >
        Rate this response
      </Button>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          How was this response?
        </Typography>
        <IconButton size="small" onClick={handleCancel}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {/* Thumbs Rating */}
        <Box>
          <Typography variant="caption" display="block" gutterBottom>
            Quick Rating
          </Typography>
          <ButtonGroup size="small">
            <Tooltip title="Thumbs Up">
              <span>
                <IconButton
                  color={thumbsValue === 1 ? 'success' : 'default'}
                  onClick={() => handleThumbsClick(1)}
                  disabled={ratingType === 'stars'}
                >
                  <ThumbUp />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Thumbs Down">
              <span>
                <IconButton
                  color={thumbsValue === -1 ? 'error' : 'default'}
                  onClick={() => handleThumbsClick(-1)}
                  disabled={ratingType === 'stars'}
                >
                  <ThumbDown />
                </IconButton>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Box>
        
        {/* Stars Rating */}
        <Box>
          <Typography variant="caption" display="block" gutterBottom>
            Detailed Rating
          </Typography>
          <MuiRating
            value={starsValue}
            onChange={(_, newValue) => {
              setStarsValue(newValue || 0);
              if (newValue && newValue > 0) {
                handleStarsClick();
              }
            }}
            disabled={ratingType === 'thumbs'}
            emptyIcon={<StarBorder fontSize="inherit" />}
            icon={<Star fontSize="inherit" />}
          />
        </Box>
      </Box>
      
      <Collapse in={showFeedback}>
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Tell us more about your experience (optional)"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || (!thumbsValue && !starsValue)}
              startIcon={isSubmitting ? undefined : <Send />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default MessageRating;